import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  FiCheckCircle,
  FiFileText,
  FiCreditCard,
  FiSearch,
  FiFilter,
  FiDownload,
  FiVideo,
  FiMessage,
} from "react-icons/fi";
import { CiCalendar } from "react-icons/ci";
import Pagination from "@mui/material/Pagination";
import { useDispatch, useSelector } from "react-redux";
import { getUserPaymentHistory } from "../../redux/actions/mindfulness";
import CustomLoader from "../Profile/pages/edit/components/CustomLoader";

// Styled Components
const Container = styled.div`
  padding: 20px;
  max-width: 80%;
  margin: 0 auto;
  margin-top: 90px;
  font-weight: 400;
  background-color: #fff;
  font-family: Montserrat;
`;

const Header = styled.div`
  margin-bottom: 20px;
  h1 {
    font-size: 24px;
    font-weight: 400;
    color: #333;
    margin-bottom: 4px;
  }
  p {
    color: #666;
    font-size: 14px;
  }
`;

const SummaryCards = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const SummaryCard = styled.div`
  background: #fff;
  border-radius: 14px;
  padding: 25px;
  flex: 1;
  min-width: 200px;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.1),
    0px 1px 2px -1px rgba(0, 0, 0, 0.1);
`;

const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => props.color || "#f0f0f0"};
  color: white;
`;

const TotalSpent = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #dcfce7;
  color: #00a63e;
`;

const TotalTransactions = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #dbeafe;
  color: #155dfc;
`;

const PaidThisMonth = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3e8ff;
  color: #9c27b0;
`;

const SummaryText = styled.div`
  h3 {
    font-size: 14px;
    color: #666;
    margin: 0;
    font-weight: 400;
  }
  p {
    font-size: 18px;
    font-weight: 400;
    margin: 5px 0 0;
  }
`;

const SearchBar = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  padding: 25px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: #fff;

  box-sizing: border-box;
  border: 1px solid rgba(229, 231, 235, 1);
  border-radius: 14px;
  box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.1),
    0px 1px 2px -1px rgba(0, 0, 0, 0.1);
  background: rgba(255, 255, 255, 1);
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  outline: none;
  transition: border 0.3s;

  box-sizing: border-box;
  border: 1px solid rgba(209, 213, 220, 1);
  border-radius: 10px;

  &:focus {
    border-color: #666;
  }
`;

const SearchButton = styled.button`
  background: #fff;
  border: none;
  border-radius: 4px;
  padding: 10px 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  color: #000;
  box-sizing: border-box;
  border: 1px solid rgba(209, 213, 220, 1);
  border-radius: 10px;
  font-size: 14px;

  &:hover {
    background: #e0e0e0;
  }
`;

const TransactionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const TransactionItem = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 15px;
`;

const Avatar = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #333;
  font-weight: 400;
  font-size: 18px;
`;

const Details = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const Name = styled.p`
  font-size: 1rem;
  font-weight: 400;
  margin: 0;
  color: #333;
`;

const Type = styled.p`
  font-size: 14px;
  color: #666;
  display: flex;
  align-items: center;
  gap: 5px;
`;

const TypeTag = styled.span`
  background: ${(props) => props.color || "#e0e0e0"};
  color: ${(props) => props.textColor || "#333"};
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  margin-right: 5px;
`;

const DateId = styled.p`
  font-size: 12px;
  color: #999;
  display: flex;
  align-items: center;
  gap: 5px;
`;

const Amount = styled.p`
  font-size: 1rem;
  font-weight: 400;
  color: #333;
  min-width: 80px;
  text-align: right;
`;

const Status = styled.span`
  background: #dcfce7;
  color: #00a63e;
  padding: 6px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 5px;
  border-radius: 10px;
`;

const ttpaymentHistoryData = {
  total_amount: 700.0,
  completed_amount: 700.0,
  total_orders: 1,
  completed_count: 1,
  failed_count: 0,
  pending_count: 0,
  has_completed: true,
  has_failed: false,
  has_pending: false,
  completed_orders: [
    {
      id: 390,
      order_id: "order_SL5KiCzgDASMTA",
      service_type: "mindfulness",
      product_id: "240",
      amount: 700.0,
      currency: "INR",
      email: "hank.m@gmail.com",
      phone_no: null,
      order_status: "success",
      message: "Payment Success. Mail Success",
      razorpay_payment_id: "pay_SL5LAoSJ0tJ8vh",
      razorpay_signature:
        "117c94951b1017a40fbc2f761950703baa9be1164547bec032c0309b0edb06c8",
      timestamp: "2026-02-27T07:19:09.353507Z",
      utimestamp: "2026-02-27",
    },
  ],
  failed_orders: [],
  pending_orders: [],
  all_orders: [
    {
      id: 390,
      order_id: "order_SL5KiCzgDASMTA",
      service_type: "mindfulness",
      product_id: "240",
      amount: 700.0,
      currency: "INR",
      email: "hank.m@gmail.com",
      phone_no: null,
      order_status: "success",
      message: "Payment Success. Mail Success",
      razorpay_payment_id: "pay_SL5LAoSJ0tJ8vh",
      razorpay_signature:
        "117c94951b1017a40fbc2f761950703baa9be1164547bec032c0309b0edb06c8",
      timestamp: "2026-02-27T07:19:09.353507Z",
      utimestamp: "2026-02-27",
    },
  ],
};

const ITEMS_PER_PAGE = 5;

const statusColors = {
  success: { bg: "#dcfce7", text: "#00a63e" },
  failed: { bg: "#fee2e2", text: "#dc2626" },
  pending: { bg: "#fef9c3", text: "#ca8a04" },
};

function UserPaymentHistory() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const dispatch = useDispatch();

  let paymentHistoryData = useSelector(
    (state) => state.mindfulness.userPaymentHistory || {},
  );

  const allOrders = paymentHistoryData.all_orders || [];

  const filteredOrders = allOrders.filter(
    (order) =>
      order.email?.toLowerCase()?.includes(search.toLowerCase()) ||
      order.order_id?.toLowerCase()?.includes(search.toLowerCase()),
  );

  const totalPages = Math.ceil(filteredOrders.length / ITEMS_PER_PAGE);
  const paginatedOrders = filteredOrders.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE,
  );

  const handlePageChange = (_, value) => {
    setPage(value);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  useEffect(async () => {
    await dispatch(getUserPaymentHistory());
    setIsLoading(false);
  }, []);

  if (isLoading) {
    return <CustomLoader />;
  }

  return (
    <Container>
      <Header>
        <h1>Payment History</h1>
        <p>View all your transaction history</p>
      </Header>

      <SummaryCards>
        <SummaryCard>
          <TotalSpent color="#4CAF50">
            <FiCheckCircle size={20} />
          </TotalSpent>
          <SummaryText>
            <h3>Total Spent</h3>
            <p>₹{paymentHistoryData?.total_amount ?? "—"}</p>
          </SummaryText>
        </SummaryCard>
        <SummaryCard>
          <TotalTransactions color="#2196F3">
            <FiFileText size={20} />
          </TotalTransactions>
          <SummaryText>
            <h3>Total Transactions</h3>
            <p>{paymentHistoryData?.total_orders ?? "—"}</p>
          </SummaryText>
        </SummaryCard>
        <SummaryCard>
          <PaidThisMonth color="#9C27B0">
            <FiCreditCard size={20} />
          </PaidThisMonth>
          <SummaryText>
            <h3>Completed</h3>
            <p>₹{paymentHistoryData?.completed_amount ?? "—"}</p>
          </SummaryText>
        </SummaryCard>
      </SummaryCards>

      <SearchBar>
        <SearchInput
          type="text"
          placeholder="Search by email or order ID..."
          value={search}
          onChange={handleSearchChange}
        />
        <SearchButton>
          <FiFilter size={16} />
          Filter
        </SearchButton>
        <SearchButton>
          <FiDownload size={16} />
          Export
        </SearchButton>
      </SearchBar>

      <TransactionList>
        {paginatedOrders.map((order) => {
          const statusKey = order.order_status?.toLowerCase();
          const colors = statusColors[statusKey] || {
            bg: "#f3f4f6",
            text: "#374151",
          };
          return (
            <TransactionItem key={order.id}>
              <Avatar>{order.email?.[0]?.toUpperCase() ?? "?"}</Avatar>
              <Details>
                <Name>{order.email}</Name>
                <Type>
                  <span>{order.service_type}</span>
                </Type>
                <DateId>
                  <CiCalendar size={16} color="#6A7282" />
                  <span>{order.utimestamp}</span>
                  <span>ID: {order.order_id}</span>
                </DateId>
              </Details>
              <Amount>₹{order.amount}</Amount>
              <Status
                style={{
                  background: colors.bg,
                  color: colors.text,
                }}
              >
                <FiCheckCircle size={16} color={colors.text} />
                {order.order_status
                  ? order.order_status.charAt(0).toUpperCase() +
                    order.order_status.slice(1)
                  : "Unknown"}
              </Status>
            </TransactionItem>
          );
        })}
      </TransactionList>

      {totalPages > 1 && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "24px",
          }}
        >
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            shape="rounded"
            color="primary"
          />
        </div>
      )}
    </Container>
  );
}

export default UserPaymentHistory;
